<script setup lang="ts">
import { ref, onMounted } from 'vue'

import { storeToRefs } from 'pinia'

import PrevStepBtn from '@/components/frontend/profile/common/PrevStepBtn.vue'

import * as aggr from '@/api-generator/aggr'
import { feUserProfileAPI } from '@/APIs'
import { ProfileSteps } from '@/constant/user'
import { EMAIL_PATTERN } from '@/constant/validation'
import appStore from '@/stores'

import type { FormInstance, FormRules, InputInstance } from 'element-plus'

const feUserProfileStore = appStore.useFEUserProfileStore
const { userInfo } = storeToRefs(appStore.useAuthStore)

interface UpdateEmailFormModel {
  newEmail: string
  confirmNewEmail: string
}

const prevStep = ref<ProfileSteps>(ProfileSteps.PROFILE_INFO)

const isLoading = ref(false)

const handleUpdateEmail = async () => {
  isLoading.value = true
  const reqBody: aggr.FEUserProfileRequestEmailChangeRq = {
    newEmail: updateEmailForm.value.newEmail,
    confirmNewEmail: updateEmailForm.value.confirmNewEmail
  }
  try {
    const { data } = await feUserProfileAPI.requestUpdateEmail(reqBody)

    feUserProfileStore.setCurrentCorrelationId(data.correlationId)
    feUserProfileStore.setNewUserEmail(updateEmailForm.value.newEmail)
    feUserProfileStore.updateCurrentStep(ProfileSteps.UPDATE_EMAIL_STEP_2)
  } catch (error: any) {
    const errorMsg = error.response.data.message ? error.response.data.message : error.message
    console.error(errorMsg)
  } finally {
    isLoading.value = false
  }
}

const updateEmailFormRef = ref<null | FormInstance>(null)

const updateEmailForm = ref<UpdateEmailFormModel>({
  newEmail: '',
  confirmNewEmail: ''
})

const validateNewEmail = (rule: any, value: string, callback: any) => {
  if (value === '') {
    callback(new Error('請輸入新信箱'))
  } else if (!EMAIL_PATTERN.test(value)) {
    callback(new Error('請輸入正確的信箱'))
  } else {
    callback()
  }
}

const validateConfirmNewEmail = (rule: any, value: string, callback: any) => {
  if (value === '') {
    callback(new Error('請再次輸入新的信箱'))
  } else if (value !== updateEmailForm.value.newEmail) {
    callback(new Error('信箱不一致'))
  } else {
    callback()
  }
}

const rules = ref<FormRules<UpdateEmailFormModel>>({
  newEmail: [{ validator: validateNewEmail, trigger: ['blur', 'change'] }],
  confirmNewEmail: [{ validator: validateConfirmNewEmail, trigger: ['blur', 'change'] }]
})

const submitForm = (formEl: FormInstance | null) => {
  if (!formEl) {
    return
  }
  formEl.validate((valid) => {
    if (valid) {
      handleUpdateEmail()
    } else {
      console.error('表單驗證失敗')
    }
  })
}

const newEmailInputRef = ref<null | InputInstance>(null)

onMounted(() => {
  newEmailInputRef.value?.focus()
})
</script>

<template>
  <div class="profile-edit-email-step1">
    <PrevStepBtn :prev-step="prevStep" />
    <div>
      <h3 class="profile-step-title">變更驗證信箱 (1/2)</h3>
      <span class="my-3 block text-xs leading-[18px] text-app-black-900">當前信箱：{{ userInfo?.email }}</span>
      <el-form
        ref="updateEmailFormRef"
        :rules="rules"
        :model="updateEmailForm"
        @keypress.enter.prevent="submitForm(updateEmailFormRef)"
      >
        <el-form-item class="mb-4" prop="newEmail">
          <label class="mb-[9px] text-xs text-app-black-600" for="newEmail">請輸入您欲變更的新信箱</label>
          <el-input ref="newEmailInputRef" v-model="updateEmailForm.newEmail" type="email"></el-input>
        </el-form-item>
        <el-form-item class="mb-4" prop="confirmNewEmail">
          <label class="mb-[9px] text-xs text-app-black-600" for="newEmail">再次確認新信箱</label>
          <el-input v-model="updateEmailForm.confirmNewEmail" type="email"></el-input>
        </el-form-item>
        <div class="profile-steps-btn-wrapper">
          <BaseButton class="w-[120px]" @click="feUserProfileStore.updateCurrentStep(ProfileSteps.PROFILE_INFO)"
            >取消
          </BaseButton>
          <BaseButton
            class="w-[120px] border-app-brand-blue-500 bg-app-brand-blue-500 text-white"
            :loading="isLoading"
            @click="submitForm(updateEmailFormRef)"
            >繼續
          </BaseButton>
        </div>
      </el-form>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.profile-edit-email-step1 {
  &-current-email {
    display: block;
    margin-top: 12px;
    margin-bottom: 12px;
    color: #000;
    font-size: 12px;
    letter-spacing: 1.147px;
  }
  &-item {
    margin-bottom: 12px;
  }
}
</style>
